import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

export interface Notification {
  id: string;
  message: string;
  category: string;
  notification_type: string;
  reference_id?: string;
  is_read: boolean;
  is_active: boolean;
  expiry_date: string;
  created_at: string;
  updated_at: string;
  recipient_id: string;
  collection_id?: string | null; // for_collection table ID - used for navigation
}

interface NotificationsContextType {
  notifications: Notification[];
  setNotifications: (notifications: Notification[] | ((prev: Notification[]) => Notification[])) => void;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  fetchNotifications: () => Promise<void>;
  unreadCount: number;
  mentionNotifications: Notification[];
  mentionUnreadCount: number;
  getCollectionIdFromNotification: (notification: Notification) => Promise<string | null>;
}

export const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);

  const unreadCount = notifications.filter(n => !n.is_read).length;
  
  // Filter mention notifications
  const mentionNotifications = notifications.filter(
    n => n.notification_type === 'comment_mention' || n.notification_type === 'update_mention'
  );
  
  const mentionUnreadCount = mentionNotifications.filter(n => !n.is_read).length;

  // Improve the getCollectionIdFromNotification function to better handle comment mentions
  const getCollectionIdFromNotification = async (notification: Notification): Promise<string | null> => {
    if (!notification) {
      console.warn('No notification provided to getCollectionIdFromNotification');
      return null;
    }
    
    console.log('Processing notification:', JSON.stringify(notification, null, 2));
    
    // For assignment notifications, reference_id is the collection ID
    if (notification.notification_type === 'assignment') {
      return notification.reference_id || null;
    }
    
    // For reminder notifications, extract collection ID from message
    if (notification.notification_type === 'reminder') {
      try {
        // Try to extract collection ID from the message text using regex
        // The format is "... (ID: xxx)"
        const idMatch = notification.message.match(/\(ID: ([^\)]+)\)/);
        
        if (idMatch && idMatch[1]) {
          console.log('Extracted collection_id from reminder message:', idMatch[1]);
          return idMatch[1];
        }
        
        // If we can't extract from message, look up the update's collection_id
        if (notification.reference_id) {
          const { data, error } = await supabase
            .from('borrower_updates')
            .select('collection_id')
            .eq('id', notification.reference_id)
            .single();
            
          if (error) {
            console.warn('Error fetching collection_id for reminder:', error);
            return null;
          }
          
          if (data && data.collection_id) {
            console.log('Found collection_id for reminder from database:', data.collection_id);
            return data.collection_id;
          }
        }
        
        console.warn('Could not extract collection_id from reminder notification');
        return null;
      } catch (error) {
        console.error('Error getting collection ID for reminder notification:', error);
        return null;
      }
    }
    
    // For update_mention notifications, reference_id points directly to the update
    if (notification.notification_type === 'update_mention') {
      try {
        // Get the borrower_updates record to get the collection_id
        const { data, error } = await supabase
          .from('borrower_updates')
          .select('collection_id')
          .eq('id', notification.reference_id)
          .single();
        
        if (error) {
          console.warn('Error fetching collection_id from borrower_updates:', error);
          return null;
        }
        
        if (!data || !data.collection_id) {
          console.warn('No collection_id found for update_mention:', notification.reference_id);
          return null;
        }
        
        console.log('Found collection_id for update_mention:', data.collection_id);
        return data.collection_id;
      } catch (error) {
        console.error('Error in getCollectionIdFromNotification for update_mention:', error);
        return null;
      }
    }
    
    // For comment_mention notifications, reference_id points to the comment
    if (notification.notification_type === 'comment_mention') {
      try {
        // Log notification details for debugging
        console.log('Processing comment_mention notification:', notification);
        
        if (!notification.reference_id) {
          console.warn('Missing reference_id in comment_mention notification');
          return null;
        }
        
        // First try to get both the update_id and collection_id with a joined query
        const { data: commentData, error: commentError } = await supabase
          .from('update_comments')
          .select(`
            *,
            borrower_updates!inner (
              collection_id
            )
          `)
          .eq('id', notification.reference_id)
          .single();
        
        // Log query results
        console.log('Comment join query result:', { data: commentData, error: commentError });
        
        if (!commentError && commentData && commentData.borrower_updates && commentData.borrower_updates.collection_id) {
          const collectionId = commentData.borrower_updates.collection_id;
          console.log('Found collection_id from joined query:', collectionId);
          return collectionId;
        }
        
        // If the join query fails, try the two-step approach
        console.log('Join query failed, trying two-step approach');
        
        // Step 1: Get the update_id from the comment
        const { data: commentRecord, error: fetchCommentError } = await supabase
          .from('update_comments')
          .select('update_id')
          .eq('id', notification.reference_id)
          .single();
        
        if (fetchCommentError || !commentRecord) {
          console.warn('Error fetching comment or comment not found:', fetchCommentError);
          return null;
        }
        
        const updateId = commentRecord.update_id;
        if (!updateId) {
          console.warn('Comment has no update_id:', notification.reference_id);
          return null;
        }
        
        console.log('Found update_id from comment:', updateId);
        
        // Step 2: Get the collection_id from the update
        const { data: updateRecord, error: fetchUpdateError } = await supabase
          .from('borrower_updates')
          .select('collection_id')
          .eq('id', updateId)
          .single();
        
        if (fetchUpdateError || !updateRecord) {
          console.warn('Error fetching update or update not found:', fetchUpdateError);
          return null;
        }
        
        const collectionId = updateRecord.collection_id;
        if (!collectionId) {
          console.warn('Update has no collection_id:', updateId);
          return null;
        }
        
        console.log('Found collection_id from update:', collectionId);
        
        // Verify the collection exists
        if (collectionId) {
          const { data: collectionData, error: collectionError } = await supabase
            .from('for_collection')
            .select('id')
            .eq('id', collectionId)
            .single();
          
          if (collectionError || !collectionData) {
            console.warn('Collection verification failed:', collectionError);
            return null;
          }
          
          console.log('Verified collection exists:', collectionId);
          return collectionId;
        }
        
        return null;
      } catch (error) {
        console.error('Error in getCollectionIdFromNotification for comment_mention:', error);
        return null;
      }
    }
    
    // For any other notification types, we don't know how to handle them
    console.warn('Unknown notification type:', notification.notification_type);
    return null;
  };

  // Define fetchNotifications before using it in useEffect
  const fetchNotifications = useCallback(async () => {
    if (!userProfile?.id) return;

    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('recipient_id', userProfile.id)
        .eq('is_active', true)  // Only get active notifications
        .or(`is_read.eq.false,notification_type.eq.assignment,notification_type.eq.collection_case,notification_type.eq.itinerary_submitted,notification_type.eq.comment_mention,notification_type.eq.update_mention,notification_type.eq.reminder`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) {
        // Filter out any notifications that are past their expiry date
        const now = new Date();
        const activeNotifications = data.filter(notification => {
          const expiryDate = new Date(notification.expiry_date);
          return expiryDate > now && (
            !notification.is_read || 
            ['assignment', 'collection_case', 'itinerary_submitted', 'comment_mention', 'update_mention', 'reminder'].includes(notification.notification_type)
          );
        });
        
        // Delete expired notifications from the database
        const expiredNotifications = data.filter(notification => {
          const expiryDate = new Date(notification.expiry_date);
          return expiryDate <= now;
        });
        
        if (expiredNotifications.length > 0) {
          const expiredIds = expiredNotifications.map(n => n.id);
          await supabase
            .from('notifications')
            .delete()
            .in('id', expiredIds);
        }
        
        setNotifications(activeNotifications);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [userProfile?.id]);

  // Update the subscription handler to handle DELETE events
  useEffect(() => {
    if (!userProfile?.id) return;

    // Initial fetch
    fetchNotifications();

    // Set up real-time subscription
    const subscription = supabase
      .channel('notifications-channel')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${userProfile.id}`
        },
        (payload) => {
          console.log('Notification change:', payload);
          if (payload.eventType === 'INSERT') {
            // Only add if it's active and not expired
            const newNotification = payload.new as Notification;
            const expiryDate = new Date(newNotification.expiry_date);
            const now = new Date();
            
            if (expiryDate > now) {
              // Display browser notification if it's a new mention
              if (
                (newNotification.notification_type === 'comment_mention' || 
                 newNotification.notification_type === 'update_mention') &&
                Notification && 
                Notification.permission === 'granted'
              ) {
                new Notification('Samofund', {
                  body: newNotification.message,
                  icon: '/samofund-logo.png'
                });
              }
              
              setNotifications(prev => [newNotification, ...prev]);
            }
          } else if (payload.eventType === 'DELETE') {
            // Remove the notification from the list
            setNotifications(prev => 
              prev.filter(notification => notification.id !== payload.old.id)
            );
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [userProfile?.id, fetchNotifications]);

  // Request notification permission on mount
  useEffect(() => {
    if (Notification && Notification.permission !== 'granted' && Notification.permission !== 'denied') {
      Notification.requestPermission();
    }
  }, []);

  const markAsRead = async (id: string) => {
    try {
      console.log('Marking notification as read:', id);
      const { error } = await supabase
        .from('notifications')
        .update({ 
          is_read: true, 
          updated_at: new Date().toISOString() 
        })
        .eq('id', id);

      if (error) {
        console.error('Error from Supabase when marking notification as read:', error);
        throw error;
      }

      console.log('Successfully marked notification as read:', id);
      setNotifications(prev =>
        prev.map(notification =>
          notification.id === id
            ? { ...notification, is_read: true }
            : notification
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  };

  const markAllAsRead = async () => {
    if (!userProfile?.id) {
      console.warn('Cannot mark all as read: No user profile');
      return;
    }
    
    const unreadNotifications = notifications.filter(n => !n.is_read);
    if (unreadNotifications.length === 0) {
      console.log('No unread notifications to mark as read');
      return;
    }
    
    try {
      console.log('Marking all notifications as read, count:', unreadNotifications.length);
      const unreadIds = unreadNotifications.map(n => n.id);
      
      const { error } = await supabase
        .from('notifications')
        .update({ 
          is_read: true, 
          updated_at: new Date().toISOString() 
        })
        .in('id', unreadIds);

      if (error) {
        console.error('Error from Supabase when marking all notifications as read:', error);
        throw error;
      }

      console.log('Successfully marked all notifications as read');
      setNotifications(prev =>
        prev.map(notification =>
          !notification.is_read
            ? { ...notification, is_read: true }
            : notification
        )
      );
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  };

  const value = {
    notifications,
    setNotifications,
    markAsRead,
    markAllAsRead,
    fetchNotifications,
    unreadCount,
    mentionNotifications,
    mentionUnreadCount,
    getCollectionIdFromNotification,
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
}; 